import CardHeader from 'components/CardHeader';
import CardWrapper from 'components/CardWrapper';
import Form from 'components/Form';
import Headline from 'components/headline';
import Select from 'components/Inputs/Select';
import Textarea from 'components/Inputs/Textarea';
import OrSeperator from 'components/OrSeperator';
import RainbowCardSmall from 'components/RainbowCard/RainbowCardSmall';
import SimplePopup from 'components/SimplePopup';
import SidebarItem from 'domain/assistant/AssistantSidebar/SidebarItem';
import SidebarItemContent from 'domain/assistant/AssistantSidebar/SidebarItemContent';
import SidebarItemHeader from 'domain/assistant/AssistantSidebar/SidebarItemHeader';
import { OrgServiceModelType } from 'models/OrgServiceModel';
import { OrgTargetGroupModelType } from 'models/OrgTargetGroupModel';
import { UserFireConfigModelType } from 'models/UserFireConfigModel';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import InnerLinkHelper from 'screens/ProjectsListScreen/InnerLinkHelper';
import { ROUTE_LAB } from 'utils/constants/routes';
import { FormType } from 'utils/hooks/useForm';

interface FireSidebarComponentProps {
  handleTargetGroupSubmit: () => void;
  handleBusinessTargetSubmit: () => void;
  handleServiceSubmit: () => void;
  userFireConfig: UserFireConfigModelType;
  form?: FormType;
  organizationId: number;
  targetGroups: OrgTargetGroupModelType[];
  services: OrgServiceModelType[];
  editable: boolean;
}

const FireSidebarComponent: React.FC<FireSidebarComponentProps> = ({
  handleTargetGroupSubmit,
  handleBusinessTargetSubmit,
  handleServiceSubmit,
  userFireConfig,
  form,
  organizationId,
  targetGroups,
  services,
  editable
}) => {
  const intl = useIntl();
  const [showServiceOverlay, setShowServiceOverlay] = useState(false);
  const [showTargetGroupOverlay, setShowTargetGroupOverlay] = useState(false);
  const [showBusinessTargetOverlay, setShowBusinessTargetOverlay] =
    useState(false);

  if (!organizationId) {
    return null;
  }

  const renderTargetGroupOverlay = () => {
    return (
      <SimplePopup
        onAbort={() => setShowTargetGroupOverlay(false)}
        onSubmit={() => {
          handleTargetGroupSubmit();
          setShowTargetGroupOverlay(false);
        }}
        headlineTextId="Fire Target group"
        submitTextId="Save"
      >
        <Form>
          <CardWrapper noSidePadding={true}>
            <InnerLinkHelper
              href={
                ROUTE_LAB + '/' + organizationId + '/settings/target-groups'
              }
              label={intl.formatMessage({ id: 'Manage target groups' })}
              noSidePadding={true}
            />
            <Select
              name="target_group"
              label={<FormattedMessage id="Target group" />}
              normalFont={true}
              onFocus={() => {
                targetGroups;
              }}
              {...form?.bindInput('target_group')}
            >
              <option value="">
                <FormattedMessage
                  id={
                    targetGroups.length === 0
                      ? 'No target groups'
                      : 'Select target group'
                  }
                />
              </option>
              {targetGroups.map((targetGroup) => (
                <option key={targetGroup.id} value={targetGroup.content!}>
                  {targetGroup.name}
                </option>
              ))}
            </Select>
          </CardWrapper>

          <OrSeperator small={true} />

          <CardWrapper noSidePadding={true}>
            <Textarea
              lines={6}
              largeFont={true}
              onChange={(e) => form?.bindInput('target_group').onChange(e)}
              placeholder={intl.formatMessage({
                id: 'Enter target group'
              })}
              name="target_group"
              normalFont={true}
              value={form?.bindInput('target_group').value}
            />
          </CardWrapper>
        </Form>
      </SimplePopup>
    );
  };

  const renderBusinessTargetOverlay = () => {
    return (
      <SimplePopup
        onAbort={() => setShowBusinessTargetOverlay(false)}
        onSubmit={() => {
          handleBusinessTargetSubmit();
          setShowBusinessTargetOverlay(false);
        }}
        headlineTextId="Fire Business target"
        submitTextId="Save"
      >
        <Form>
          <CardWrapper noSidePadding={true}>
            <Textarea
              lines={6}
              largeFont={true}
              onChange={(e) => form?.bindInput('business_target').onChange(e)}
              placeholder={intl.formatMessage({
                id: 'Enter business target'
              })}
              name="business_target"
              normalFont={true}
              value={form?.bindInput('business_target').value}
            />
          </CardWrapper>
        </Form>
      </SimplePopup>
    );
  };

  const renderServiceOverlay = () => {
    return (
      <SimplePopup
        onAbort={() => setShowServiceOverlay(false)}
        onSubmit={() => {
          handleServiceSubmit();
          setShowServiceOverlay(false);
        }}
        headlineTextId="Fire Service"
        submitTextId="Save"
      >
        <Form>
          <CardWrapper noSidePadding={true}>
            <InnerLinkHelper
              href={ROUTE_LAB + '/' + organizationId + '/settings/services'}
              label={intl.formatMessage({ id: 'Manage services short' })}
              noSidePadding={true}
            />
            <Select
              name="service"
              label={<FormattedMessage id="Service short" />}
              normalFont={true}
              onFocus={() => {
                services;
              }}
              {...form?.bindInput('service')}
            >
              <option value="">
                <FormattedMessage
                  id={services.length === 0 ? 'No services' : 'Select service'}
                />
              </option>
              {services.map((service) => (
                <option key={service.id} value={service.content!}>
                  {service.name}
                </option>
              ))}
            </Select>
          </CardWrapper>

          <OrSeperator small={true} />

          <CardWrapper noSidePadding={true}>
            <Textarea
              lines={6}
              largeFont={true}
              onChange={(e) => form?.bindInput('service').onChange(e)}
              placeholder={intl.formatMessage({
                id: 'Enter service'
              })}
              name="service"
              normalFont={true}
              value={form?.bindInput('service').value}
            />
          </CardWrapper>
        </Form>
      </SimplePopup>
    );
  };

  return (
    <>
      {showServiceOverlay && renderServiceOverlay()}
      {showTargetGroupOverlay && renderTargetGroupOverlay()}
      {showBusinessTargetOverlay && renderBusinessTargetOverlay()}

      <div className="fire-sidebar">
        <Headline opaque={true} size="M">
            <FormattedMessage id="assistant sidebar fire" />
          </Headline>

          <SidebarItem isStatic={!editable}>
            <SidebarItemHeader opaque={true}>
              <FormattedMessage id="assistant sidebar fire business target" />
            </SidebarItemHeader>
            <SidebarItemContent>
              <RainbowCardSmall
                onClick={() => {
                  if (editable) {
                    setShowBusinessTargetOverlay(true);
                  }
                }}
              >
                <CardHeader
                  title={<FormattedMessage id="Fire Business target" />}
                  simple={true}
                />
                <p className="typography typography--small">
                  {userFireConfig.business_target}
                </p>
              </RainbowCardSmall>
            </SidebarItemContent>
          </SidebarItem>

          <SidebarItem isStatic={!editable}>
            <SidebarItemHeader opaque={true}>
              <FormattedMessage id="assistant sidebar fire target group" />
            </SidebarItemHeader>
            <SidebarItemContent>
              <RainbowCardSmall
                onClick={() => {
                  if (editable) {
                    setShowTargetGroupOverlay(true);
                  }
                }}
              >
                <CardHeader
                  title={<FormattedMessage id="Fire Target group" />}
                  simple={true}
                />
                <p className="typography typography--small">
                  {userFireConfig.target_group}
                </p>
              </RainbowCardSmall>
            </SidebarItemContent>
          </SidebarItem>

          <SidebarItem isStatic={!editable}>
            <SidebarItemHeader opaque={true}>
              <FormattedMessage id="assistant sidebar fire service" />
            </SidebarItemHeader>
            <SidebarItemContent>
              <RainbowCardSmall
                onClick={() => {
                  if (editable) {
                    setShowServiceOverlay(true);
                  }
                }}
              >
                <CardHeader
                  title={<FormattedMessage id="Fire Service" />}
                  simple={true}
                />

                <p className="typography typography--small">
                  {userFireConfig.service}
                </p>
              </RainbowCardSmall>
            </SidebarItemContent>
          </SidebarItem>
      </div>
    </>
  );
};

export default FireSidebarComponent;
